"use client";

import { motion, useReducedMotion } from "framer-motion";
import { UserCheck, PiggyBank, Zap, Target, ShieldCheck, MessageSquare, ArrowRight, Check, X, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const benefitsList = [{
  id: "access",
  icon: UserCheck,
  title: "1-on-1 Senior Expert Access",
  highlight: "Direct Expertise",
  consultant: "You work directly with me, Yassine Essaadi. No account managers, no junior delegates, and no communication barrier.",
  agency: "Pitched by senior directors, then quietly handed off to junior account executives with limited experience.",
  impact: "Direct access to senior strategy and immediate answers."
}, {
  id: "cost",
  icon: PiggyBank,
  title: "Lean & Cost-Efficient Investment",
  highlight: "Zero Agency Overhead",
  consultant: "100% of your budget goes into high-impact SEO work. You pay for my expertise, not expensive office spaces or sales commissions.",
  agency: "Retainers are inflated to cover heavy corporate overhead, multi-layered management, and aggressive sales teams.",
  impact: "Maximum return on investment for every pound spent."
}, {
  id: "agility",
  icon: Zap,
  title: "Agile & Fast Execution",
  highlight: "Rapid Response",
  consultant: "Direct decision-making and immediate strategy adjustments whenever Google updates algorithms or market priorities shift.",
  agency: "Pivoting requires internal meetings, multi-level sign-offs, and delayed ticket queues that stall your momentum.",
  impact: "Campaigns move at the true speed of search engines."
}, {
  id: "tailored",
  icon: Target,
  title: "Tailored SEO Strategy",
  highlight: "Bespoke Deliverables",
  consultant: "Custom-built organic search roadmaps specifically crafted around your target audience, tech stack, and pipeline revenue targets.",
  agency: "Cookie-cutter monthly checklists applied blindly across dozens of clients in completely different industries.",
  impact: "Strategy engineered specifically for your commercial success."
}, {
  id: "reporting",
  icon: ShieldCheck,
  title: "Honest & Clear Measurement",
  highlight: "Pipeline Focus",
  consultant: "Plain-English monthly reports highlighting actual organic conversions, revenue impact, and clear next steps.",
  agency: "Automated 40-page PDF reports filled with vanity metrics that obscure actual commercial results.",
  impact: "Unmatched clarity on organic ROI and search growth."
}, {
  id: "commitment",
  icon: MessageSquare,
  title: "Dedicated Client Focus",
  highlight: "Selective Capacity",
  consultant: "I intentionally cap my active client roster so that your business receives my focused energy, proactive ideas, and care.",
  agency: "Account managers balance 15 to 25 accounts simultaneously, leading to reactive standard maintenance.",
  impact: "A proactive strategic partner who truly cares about your business."
}];
export default function Consultant() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="consultantvsagencybenefits" data-nav="light" className="relative py-20 lg:py-28 bg-background text-foreground overflow-hidden">
      <div className="absolute top-12 left-1/2 -translate-x-1/2 w-full max-w-7xl h-96 bg-secondary/40 blur-3xl rounded-full pointer-events-none -z-10" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-20">
          <Badge variant="secondary" className="mb-4 px-3.5 py-1 text-sm bg-secondary text-secondary-foreground border border-accent/20">
            <Sparkles className="size-3.5 text-accent mr-1.5 inline" />
            Why Work Directly With Me
          </Badge>
          
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground tracking-tight mb-6">
            Independent Consultant vs Traditional Agency
          </h2>

          <p className="text-lg text-muted-foreground leading-relaxed">
            When you hire an SEO agency, you often pay for account managers, executive salaries, and city-centre offices.
            When you hire me, <strong className="text-foreground font-semibold">you work directly with me</strong>. 
            Here is how my focused freelance model delivers superior value and results for your business.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {benefitsList.map((benefit, i) => {
          const IconComponent = benefit.icon;
          return <motion.div key={benefit.id} variants={itemVariants} data-index={i} className="flex">
                <Card className="flex flex-col justify-between w-full border border-border bg-card text-card-foreground shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-300 hover:border-primary/40 rounded-xl overflow-hidden">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                        <IconComponent className="size-6" />
                      </div>
                      <Badge variant="outline" className="text-xs bg-secondary/60 text-foreground border-accent/30 font-medium">
                        {benefit.highlight}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-bold font-serif text-card-foreground">
                      {benefit.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-4 flex-1 flex flex-col justify-between">
                    <div className="space-y-3 text-sm">
                      <div className="p-3.5 rounded-lg bg-secondary/30 border border-border/60">
                        <div className="flex items-center text-xs font-semibold text-primary mb-1.5 uppercase tracking-wider">
                          <Check className="size-3.5 mr-1 text-primary shrink-0" />
                          With Me (Yassine)
                        </div>
                        <p className="text-foreground/90 font-medium leading-normal">
                          {benefit.consultant}
                        </p>
                      </div>

                      <div className="p-3.5 rounded-lg bg-muted/50 border border-border/40">
                        <div className="flex items-center text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">
                          <X className="size-3.5 mr-1 text-muted-foreground shrink-0" />
                          Typical Agency
                        </div>
                        <p className="text-muted-foreground leading-normal">
                          {benefit.agency}
                        </p>
                      </div>
                    </div>

                    <Separator className="my-2 bg-border/60" />

                    <div className="pt-1">
                      <p className="text-xs font-semibold text-foreground/80 flex items-center">
                        <span className="w-1.5 h-1.5 rounded-full bg-accent mr-2 shrink-0" />
                        Impact: <span className="ml-1 text-muted-foreground font-normal">{benefit.impact}</span>
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        <div className="mt-16 lg:mt-20 p-8 lg:p-10 rounded-2xl bg-secondary/40 border border-accent/30 shadow-sm max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-8 text-center md:text-left">
            <div className="relative shrink-0">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden border-2 border-primary shadow-md">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_consultant_portrait.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_consultant_portrait.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_consultant_portrait.png-wvc-sizes" alt="Yassine Essaadi - Freelance SEO Consultant" className="w-full h-full object-cover" loading="lazy" decoding="async" />
              </div>
              <span className="absolute bottom-0 right-0 p-1 bg-primary text-primary-foreground rounded-full shadow">
                <Check className="size-3.5" />
              </span>
            </div>

            <div className="flex-1 space-y-3">
              <h3 className="text-xl sm:text-2xl font-bold font-serif text-foreground">
                {"My Personal Guarantee: Direct Accountability"}
              </h3>
              <p className="text-muted-foreground leading-relaxed text-sm sm:text-base">
                {"\"I take pride in giving every single client my complete personal attention. When you partner with YE Consultancy, you deal exclusively with me—from the initial audit to strategy implementation and monthly execution. No excuses, no middle managers, just transparent expertise working to scale your organic search revenue.\""}
              </p>
              <p className="text-xs font-bold text-primary uppercase tracking-wider">
                Yassine Essaadi — Independent Freelance SEO Consultant
              </p>
            </div>

            <div className="shrink-0 flex flex-col sm:flex-row md:flex-col gap-3 w-full sm:w-auto">
              <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-md">
                <Link to="/ContactPage">
                  Book Free Consultation
                  <ArrowRight className="size-4 ml-1.5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-border hover:bg-background">
                <Link to="/CaseStudiesPage">
                  Explore Case Studies
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}